/**
 * React Query hooks for Google Search Console (AB#297).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { searchApi } from '../api/search';
import { isTauri, openExternal } from '../utils';

export const searchKeys = {
  all: (siteSlug: string) => ['sites', siteSlug, 'search'] as const,
  status: (siteSlug: string) => [...searchKeys.all(siteSlug), 'status'] as const,
  analytics: (siteSlug: string) => [...searchKeys.all(siteSlug), 'analytics'] as const,
};

export function useSearchStatus(siteSlug: string) {
  return useQuery({
    queryKey: searchKeys.status(siteSlug),
    queryFn: () => searchApi.getStatus(siteSlug),
    enabled: !!siteSlug,
  });
}

export function useSearchAnalytics(siteSlug: string, enabled = true) {
  return useQuery({
    queryKey: searchKeys.analytics(siteSlug),
    queryFn: () => searchApi.getAnalytics(siteSlug),
    enabled: !!siteSlug && enabled,
  });
}

/**
 * Fetches the consent URL and sends the user to Google's OAuth screen.
 *
 * AB#490 desktop: Google refuses OAuth in embedded webviews
 * (`disallowed_useragent`), so hand the consent URL to the system browser.
 * Token exchange is server-side (no deep-link needed); the Search page re-polls
 * connection status on window focus. Web keeps the same-window redirect.
 */
export function useConnectSearch(siteSlug: string) {
  return useMutation({
    mutationFn: () => searchApi.connect(siteSlug),
    onSuccess: (res) => {
      const url = res.data?.url;
      if (!url) return;
      if (isTauri()) void openExternal(url);
      else window.location.href = url;
    },
  });
}

/** Forgets the stored Google tokens for this site. */
export function useDisconnectSearch(siteSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => searchApi.disconnect(siteSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: searchKeys.all(siteSlug) });
    },
  });
}
